import React, { useEffect, useState, useContext } from 'react';
import { BarMouseOver, BarMouseOverState } from './BarMouseOver';
import { initialLegendData } from './liquiditySummaryChartLegendData';
import { LiquiditySummaryChartData } from './liquiditySummaryChartData';
import {
    LiquiditySummaryChartLegendData,
    getLegendData,
} from './liquiditySummaryChartLegendData';
import Legend from 'utils/components/Legend/Legend';
import classNames from 'classnames';
import AppContext from 'app/AppContext';
import {
    percentCloseTooltip,
    auctionOpenTooltip,
    auctionCloseTooltip,
    histVolRodTooltip,
    histVolTilNowTooltip,
    leavesTooltip,
    percentPredictedRemainingVolTooltip,
    todaysVolRODTooltip,
    todaysVolTilNowTooltip,
    todaysAdjTooltip,
    todaysPredictedAdjTooltip,
    auctionPredTooltip,
} from '../help/liquiditySummaryLegendTootip';
import LiquiditySummaryChartHelp from 'widgets/liquiditySummary/help/LiquiditySummaryChartHelp';
import { LiquiditySummaryConfiguration } from 'widgets/liquiditySummary/store/configuration/LiquiditySummaryConfiguration';
import { WidgetLegend } from 'widgets/WidgetLegend/WidgetLegend';

export interface LiquiditySummaryChartLegendsProps {
    data: LiquiditySummaryChartData;
    mouseOver: BarMouseOver;
    symbol: string;
    isDefault: boolean;
}

export default function LiquiditySummaryChartLegends(
    props: LiquiditySummaryChartLegendsProps
) {
    const appContext = useContext(AppContext);

    const { data, mouseOver, symbol, isDefault } = props;
    const [legendData, setLegendData] =
        useState<LiquiditySummaryChartLegendData>(initialLegendData);

    const [mouseOverState, setMouseOverState] = useState<BarMouseOverState>({});

    useEffect(() => {
        mouseOver.subscribe(
            'LiquiditySummaryChartLegends',
            (mouseOverState) => {
                setMouseOverState(mouseOverState || {});
            }
        );
    });

    useEffect(() => {
        setLegendData(getLegendData(appContext.numberFormat, data, isDefault));
    }, [symbol, data, mouseOverState, appContext, isDefault]);

    return (
        <div
            className={classNames(
                'LiquiditySummaryChartLegends',
                mouseOverState.bar,
                mouseOverState.key
            )}
        >
            <div className="legend-group">
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartOrderLeavesLegend">
                    <Legend
                        className="order-leaves LegendItem first"
                        value={legendData.orderRem}
                        label="Order leaves"
                        tooltip={leavesTooltip}
                    />
                </WidgetLegend>
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartRodLegend">
                    <Legend
                        className="orders-rod LegendItem"
                        value={legendData.percentPredRemVol}
                        label="% PRV"
                        tooltip={percentPredictedRemainingVolTooltip}
                    />
                </WidgetLegend>
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartAcvLegend">
                    <Legend
                        className="orders-acv LegendItem last"
                        value={legendData.percentPredClose}
                        label="% PCV"
                        tooltip={percentCloseTooltip}
                    />
                </WidgetLegend>
            </div>
            <div className="legend-column">
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartHistVolLegend">
                    <Legend
                        className="historical-volume now"
                        value={legendData.tilNow.historicalVolume}
                        twoColourBorder={true}
                        label="Hist Vol til now"
                        tooltip={histVolTilNowTooltip}
                    />
                </WidgetLegend>
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartTodaysVolLegend">
                    <Legend
                        className="todays-volume now"
                        value={legendData.tilNow.actualVolume}
                        label="Act Vol til now"
                        tooltip={todaysVolTilNowTooltip}
                    />
                </WidgetLegend>

                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartTodaysAdjLegend">
                    <Legend
                        className="todays-adj"
                        value={legendData.tilNow.todaysAdj}
                        label="Adj til now"
                        tooltip={todaysAdjTooltip}
                    />
                </WidgetLegend>
            </div>

            <div className="legend-column">
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartHistVolRodLegend">
                    <Legend
                        className="historical-volume rod"
                        twoColourBorder={true}
                        value={legendData.restOfDay.historicalVolume}
                        label="Hist Vol ROD"
                        tooltip={histVolRodTooltip}
                    />
                </WidgetLegend>
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartTodaysVolRodLegend">
                    <Legend
                        className="remaining-volume"
                        value={legendData.restOfDay.actualVolume}
                        label="Act Vol ROD"
                        tooltip={todaysVolRODTooltip}
                    />
                </WidgetLegend>
                <WidgetLegend<LiquiditySummaryConfiguration> hideLegendPropertyName="hideChartExpAdjLegend">
                    <Legend
                        className="todays-adj"
                        value={legendData.restOfDay.todaysAdj}
                        label="Adj ROD"
                        tooltip={todaysPredictedAdjTooltip}
                    />
                </WidgetLegend>
            </div>
            <div className="legend-column">
                <WidgetLegend<LiquiditySummaryConfiguration>
                    hideLegendPropertyName="hideChartAuctionOpenVolLegend"
                    alignDropdown={'right'}
                >
                    <Legend
                        className="auction open"
                        value={legendData.historicalOpenAuction}
                        label="Hist Open Auction"
                        tooltip={auctionOpenTooltip}
                    />
                </WidgetLegend>
                <WidgetLegend<LiquiditySummaryConfiguration>
                    hideLegendPropertyName="hideChartAuctionVolLegend"
                    alignDropdown={'right'}
                >
                    <Legend
                        className="auction close"
                        value={legendData.historicalCloseAuction}
                        label="Hist Cls Auction"
                        tooltip={auctionCloseTooltip}
                    />
                </WidgetLegend>
                <WidgetLegend<LiquiditySummaryConfiguration>
                    hideLegendPropertyName="hideChartPredAuctionVolLegend"
                    alignDropdown={'right'}
                >
                    <Legend
                        className="auction close"
                        value={legendData.expectedCloseAuction}
                        label="Pred Cls Auction"
                        tooltip={auctionPredTooltip}
                    />
                </WidgetLegend>
            </div>
            <LiquiditySummaryChartHelp />
        </div>
    );
}
